import { Component, ElementRef, OnInit, ViewChild, NgZone, ChangeDetectorRef, NO_ERRORS_SCHEMA, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../auth/auth-service';
import { CommonModule } from '@angular/common';
import { DatePickerModule } from 'primeng/datepicker';
import { ClassNamesModule } from 'primeng/classnames';
import { ToastService } from '../services/toast-service';
import { getAnalytics, logEvent } from '@angular/fire/analytics';
import { FirebaseApp } from '@angular/fire/app';
import { WorkshopService } from '../services/workshop-service';


@Component({
  selector: 'app-home-page',
  templateUrl: './home-page.html',
  styleUrl: './home-page.css',
  imports: [CommonModule, ReactiveFormsModule, DatePickerModule, ClassNamesModule],
  standalone: true,
  schemas: [NO_ERRORS_SCHEMA]
})
export class HomePage implements OnInit {
  @ViewChild('popup') myDiv!: ElementRef;
  students: string | number = 0;
  mentors: string | number = 0;
  courses: string | number = 0;
  registerForm!: FormGroup
  minDate = new Date(2000, 0, 1);
  errorMessage!: string;
  successMessage!: string;
  app = inject(FirebaseApp);    // get the already-created app
  analytics = getAnalytics(this.app);
  workshop: any
  constructor(private fb: FormBuilder, private toastService: ToastService, private authService: AuthService, private router: Router, private ngZone: NgZone, private cdr: ChangeDetectorRef, private workshopService: WorkshopService) {
    this.registerForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phoneNumber: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      collegeName: ['', [Validators.required, Validators.maxLength(50)]],
      universityName: ['', [Validators.required, Validators.maxLength(50)]],
      degreeandBranch: ['', [Validators.required, Validators.maxLength(50)]],
      passingYear: ['', Validators.required]
    })
  }

  navigateAndScroll(route: string, elementId: string) {
    this.router.navigate([route]).then(() => {
      const el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  }

  ngOnInit() {
    this.animateValue(0, 15000, 1500, (val) => {
      this.students = val >= 1000 ? Math.floor(val / 1000) + 'k+' : val;
      this.cdr.detectChanges(); // 👈 force Angular to refresh UI
    });
    this.animateValue(0, 500, 1500, (val) => {
      this.mentors = val;
      this.cdr.detectChanges();
    });
    this.animateValue(0, 100, 1500, (val) => {
      this.courses = val;
      this.cdr.detectChanges();
    });

    this.workshopService.getLatestWorkshop()
      .then((res: any) => {
        this.workshop = res;
        console.log(this.workshop);
      })
      .catch((err: any) => {
        console.error(err);
      });

    this.checkVerification();

    setInterval(() => {
      this.checkVerification();
    }, 3000);
  }

  formatTimeRange(timeFrom: string, timeTo: string): string {
    if (!timeFrom || !timeTo) return '';

    const from = new Date(timeFrom);
    const to = new Date(timeTo);

    if (isNaN(from.getTime()) || isNaN(to.getTime())) return '';

    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    };

    const fromTime = from.toLocaleTimeString('en-IN', options);
    const toTime = to.toLocaleTimeString('en-IN', options);

    return `${fromTime} - ${toTime} IST`;
  }

  formatDateRange(dates: Date[]): string {
    if (!dates || dates.length < 2) return '';

    const start = dates[0];
    const end = dates[1];

    if (!(start instanceof Date) || !(end instanceof Date)) return '';

    const startDay = start.getDate();
    const endDay = end.getDate();

    const startMonth = start.toLocaleString('en-IN', { month: 'long' });
    const endMonth = end.toLocaleString('en-IN', { month: 'long' });

    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    // ✅ Same month & same year
    if (startMonth === endMonth && startYear === endYear) {
      return `${startMonth} ${startDay}-${endDay}, ${startYear}`;
    }

    // ✅ Same year, different month
    if (startYear === endYear) {
      return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${startYear}`;
    }

    // ✅ Different year
    return `${startMonth} ${startDay}, ${startYear} - ${endMonth} ${endDay}, ${endYear}`;
  }

  private animateValue(start: number, end: number, duration: number, update: (val: number) => void) {
    this.ngZone.runOutsideAngular(() => {
      const range = end - start;
      let current = start;
      const increment = Math.ceil(range / (duration / 20));
      const stepTime = 20;

      const timer = setInterval(() => {
        current += increment;
        if (current >= end) {
          current = end;
          clearInterval(timer);
        }

        // update UI
        this.ngZone.run(() => update(current));
      }, stepTime);
    });
  }

  alreadyVerified = false;

  async checkVerification() {
    if (this.alreadyVerified) return;

    const updated = await this.authService.checkVerificationStatus();

    if (updated) {
      this.alreadyVerified = true;

      this.toastService.showSuccess(
        "Welcome to ChalkBasket. Your registration has been completed."
      );
    }
  }

  trackRegistrationClick() {
    // Firebase google analytics event for registration button click     
    logEvent(this.analytics, 'registration_click');
    console.log('registration_click event logged');
  }

  async submit() {
    this.registerForm.markAllAsTouched();

    if (this.registerForm.invalid) { return; }

    const data = {
      ...this.registerForm.value
    }
    data.passingYear = data.passingYear.toISOString('en-CA').split('T')[0];

    const isDuplicate = await this.authService.checkDuplicateEmail(data.email!)
    if (isDuplicate) {
      this.toastService.showError("Email already registered!");
      return;
    }

    const res = await this.authService.registerStudent(data);

    if (res.success) {
      this.toastService.showSuccess(res.message);
    } else {
      this.toastService.showError(res.message);
    }

    // FIRE GA4 event here for registration complete    
    logEvent(this.analytics, 'registration_completed', {
      user_name: data.name
    });
    this.registerForm.reset();
  }

  hidePopup() {
    this.myDiv.nativeElement.classList.remove('popup');
    this.myDiv.nativeElement.classList.add('popdown');
  }

  navigatetoPath(path: string) {
    this.router.navigate(['/' + path])
  }
}
